// ============================================================
// AccessGate — full auth + allowlist gate for the shell.
//
// Two-layer check:
//   1. Is the user authenticated? If not → redirect to login.
//   2. Is the user on the approved allowlist? If not → AccessDenied.
//
// Shows diagnostic authStep so operators can see exactly which
// stage is running. Includes a 15s safety timeout so the gate
// never hangs forever.
// ============================================================

import type { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from './AuthContext'
import { buildLoginHref } from './redirect'
import AccessDenied from '../routes/AccessDenied'

interface AccessGateProps {
  children: ReactNode
}

export default function AccessGate({ children }: AccessGateProps) {
  const { user, loading, approved, authStep } = useAuth()
  const location = useLocation()
  const next = `${location.pathname}${location.search}`

  // Still loading auth state
  if (loading) {
    return (
      <GateScreen
        label="AUTHENTICATING"
        step={authStep}
      />
    )
  }

  // Not authenticated → redirect to GitHub login
  if (!user) {
    return <Navigate to={buildLoginHref(next)} replace />
  }

  // Authenticated but allowlist check still running
  if (approved === null) {
    return (
      <GateScreen
        label="VERIFYING ACCESS"
        step={authStep}
      />
    )
  }

  // Authenticated but not approved → show denial
  if (!approved) {
    return <AccessDenied />
  }

  // Authenticated and approved → render the shell
  return <>{children}</>
}

function GateScreen({ label, step }: { label: string; step: string }) {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--olv-void)',
        gap: 12,
      }}
    >
      <div
        style={{
          color: 'var(--olv-text-tertiary)',
          fontFamily: 'var(--font-mono)',
          fontSize: 11,
          letterSpacing: '0.10em',
        }}
      >
        {label}…
      </div>
      <div
        style={{
          color: 'var(--olv-text-tertiary)',
          fontFamily: 'var(--font-mono)',
          fontSize: 9,
          letterSpacing: '0.08em',
          opacity: 0.5,
        }}
      >
        {step}
      </div>
    </div>
  )
}
